/**
 * Whiteboard Export Utilities
 * static/js/whiteboard/export.js
 * 
 * PNG/SVG/JSON 匯出功能
 */

import { EXPORT_CONFIG, MESSAGES } from './config.js';
import { getExcalidrawAPI, getCurrentTitle } from './state.js';
import { showToast } from './ui.js';
import { blobToBase64 } from './thumbnail.js';

/**
 * 將 Excalidraw files 轉換為可序列化格式
 * Excalidraw getFiles() 可能返回 Map 或包含非序列化屬性的對象
 * @param {Object|Map} rawFiles 
 * @returns {Object}
 */
export function serializeFiles(rawFiles) {
  const files = {};
  if (!rawFiles) {
    return files;
  }
  
  // 如果是 Map，轉換為物件
  const filesObj = rawFiles instanceof Map ? Object.fromEntries(rawFiles) : rawFiles;
  
  for (const [id, file] of Object.entries(filesObj)) {
    if (file && typeof file === 'object') {
      // 只保留可序列化的屬性
      files[id] = {
        id: file.id,
        mimeType: file.mimeType,
        dataURL: file.dataURL,
        created: file.created,
        lastRetrieved: file.lastRetrieved,
      };
    }
  }
  
  return files;
}

/**
 * 匯出為 PNG
 * @param {Object} options
 * @returns {Promise<void>}
 */
export async function exportToPng(options = {}) {
  const api = getExcalidrawAPI();
  if (!api) {
    showToast(MESSAGES.EXPORT_FAILED, 'error');
    return;
  }

  try {
    const elements = api.getSceneElements();
    const appState = api.getAppState();
    const files = api.getFiles();

    if (!elements || elements.length === 0) {
      showToast('白板是空的，無法匯出', 'warning');
      return;
    }

    const scale = options.scale || 2;
    const filename = sanitizeFilename(options.filename || getCurrentTitle()) + EXPORT_CONFIG.PNG.extension;

    if (typeof window.ExcalidrawLib !== 'undefined' && window.ExcalidrawLib.exportToBlob) {
      const blob = await window.ExcalidrawLib.exportToBlob({
        elements,
        appState: {
          ...appState,
          exportBackground: true,
          exportWithDarkMode: false,
          viewBackgroundColor: '#ffffff',
          exportScale: scale,
        },
        files,
        mimeType: EXPORT_CONFIG.PNG.mimeType,
        quality: EXPORT_CONFIG.PNG.quality,
      });

      downloadBlob(blob, filename);
      showToast('PNG 已匯出', 'success');
    } else {
      // 回退方案
      await exportFromSvgElement('png', filename);
    }
  } catch (error) {
    console.error('Error exporting PNG:', error);
    showToast(MESSAGES.EXPORT_FAILED, 'error');
  }
}

/**
 * 匯出為 SVG
 * @param {Object} options
 * @returns {Promise<void>}
 */
export async function exportToSvg(options = {}) {
  const api = getExcalidrawAPI();
  if (!api) {
    showToast(MESSAGES.EXPORT_FAILED, 'error');
    return;
  }

  try {
    const elements = api.getSceneElements();
    const appState = api.getAppState();
    const files = api.getFiles();

    if (!elements || elements.length === 0) {
      showToast('白板是空的，無法匯出', 'warning');
      return;
    }

    const filename = sanitizeFilename(options.filename || getCurrentTitle()) + EXPORT_CONFIG.SVG.extension;

    if (typeof window.ExcalidrawLib !== 'undefined' && window.ExcalidrawLib.exportToSvg) {
      const svgElement = await window.ExcalidrawLib.exportToSvg({
        elements,
        appState: {
          ...appState,
          exportBackground: true,
          exportWithDarkMode: false,
          viewBackgroundColor: '#ffffff',
        },
        files,
      });

      const svgString = svgElement.outerHTML;
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      downloadBlob(blob, filename);
      showToast('SVG 已匯出', 'success');
    } else {
      // 回退方案
      await exportFromSvgElement('svg', filename);
    }
  } catch (error) {
    console.error('Error exporting SVG:', error);
    showToast(MESSAGES.EXPORT_FAILED, 'error');
  }
}

/**
 * 匯出為 JSON（.excalidraw 格式）
 * @param {Object} options
 * @returns {Promise<void>}
 */
export async function exportToJson(options = {}) {
  const api = getExcalidrawAPI();
  if (!api) {
    showToast(MESSAGES.EXPORT_FAILED, 'error');
    return;
  }

  try {
    const elements = api.getSceneElements();
    const appState = api.getAppState();
    const rawFiles = api.getFiles();
    const files = serializeFiles(rawFiles);

    const filename = sanitizeFilename(options.filename || getCurrentTitle()) + EXPORT_CONFIG.JSON.extension;

    let jsonString;

    if (typeof window.ExcalidrawLib !== 'undefined' && window.ExcalidrawLib.serializeAsJSON) {
      jsonString = window.ExcalidrawLib.serializeAsJSON(elements, appState, rawFiles, 'local');
    } else {
      // 手動建立 JSON
      const data = {
        type: 'excalidraw',
        version: 2,
        source: window.location.origin,
        elements: elements,
        appState: {
          viewBackgroundColor: appState.viewBackgroundColor || '#ffffff',
          gridSize: appState.gridSize,
        },
        files: files,
      };
      jsonString = JSON.stringify(data, null, 2);
    }

    const blob = new Blob([jsonString], { type: EXPORT_CONFIG.JSON.mimeType });
    downloadBlob(blob, filename);
    showToast('JSON 已匯出', 'success');
  } catch (error) {
    console.error('Error exporting JSON:', error);
    showToast(MESSAGES.EXPORT_FAILED, 'error');
  }
}

/**
 * 從 SVG 元素匯出（回退方案）
 * @param {string} format - 'png' | 'svg'
 * @param {string} filename
 */
async function exportFromSvgElement(format, filename) {
  const svgElement = document.querySelector('#excalidraw-container svg');
  if (!svgElement) {
    throw new Error('SVG element not found');
  }

  const svgClone = svgElement.cloneNode(true);
  const svgData = new XMLSerializer().serializeToString(svgClone);

  if (format === 'svg') {
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    downloadBlob(blob, filename);
    return;
  }

  // PNG 格式
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  const img = new Image();
  
  return new Promise((resolve, reject) => {
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = 2;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        URL.revokeObjectURL(svgUrl);
        downloadBlob(blob, filename);
        resolve();
      }, 'image/png');
    };

    img.onerror = (error) => {
      URL.revokeObjectURL(svgUrl);
      reject(error);
    };

    img.src = svgUrl;
  });
}

/**
 * 下載 Blob
 * @param {Blob} blob
 * @param {string} filename
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 清理檔名
 * @param {string} filename
 * @returns {string}
 */
export function sanitizeFilename(filename) {
  if (!filename) return 'whiteboard';
  return filename
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '_')
    .substring(0, 100);
}

/**
 * 取得場景 JSON 字串
 * @returns {string|null}
 */
export function getSceneJson() {
  const api = getExcalidrawAPI();
  if (!api) return null;

  try {
    const elements = api.getSceneElements();
    const appState = api.getAppState();
    const rawFiles = api.getFiles();
    const files = serializeFiles(rawFiles);

    const data = {
      elements: elements || [],
      appState: {
        viewBackgroundColor: appState?.viewBackgroundColor || '#ffffff',
        gridSize: appState?.gridSize,
      },
      files: files,
    };

    return JSON.stringify(data);
  } catch (error) {
    console.error('Error getting scene JSON:', error);
    return null;
  }
}
